import { Sort } from '@compass/client/types/common';
import {
  type CallDirection,
  type Evaluation,
  EvaluationStatus,
  type ListEvaluationsRequest,
} from '@compass/client/types/evaluation';
import { useQuery } from '@tanstack/react-query';

import type { SupportedTimeZone } from '@/constants/date';
import { evaluationService } from '@/services/api';
import { naiveDateToUtc } from '@/utils/date';

import { analyticsQueryKeys } from './useOrgAnalytics';

interface UseAgentRecentEvaluationsArgs {
  organizationId: string | null;
  agentId: string | null;
  programIds?: string[];
  callDirections?: string[];
  start?: string;
  end?: string;
  timeZone: SupportedTimeZone;
  limit?: number;
}

const normalizeIds = (values?: string[]) => [...new Set(values?.filter(Boolean) ?? [])].sort();

const buildRecentEvaluationsRequest = ({
  organizationId,
  agentId,
  programIds,
  callDirections,
  start,
  end,
  timeZone,
  limit = 10,
}: UseAgentRecentEvaluationsArgs): ListEvaluationsRequest | null => {
  if (!organizationId || !agentId) return null;

  const normalizedProgramIds = normalizeIds(programIds);

  return {
    organization_id: organizationId,
    agent_user_ids: [agentId],
    program_ids: normalizedProgramIds.length ? normalizedProgramIds : undefined,
    call_directions: callDirections?.length
      ? callDirections.map(value => Number(value) as CallDirection)
      : undefined,
    statuses: [EvaluationStatus.EVALUATION_STATUS_COMPLETED],
    call_start_time_after: start ? naiveDateToUtc(start, timeZone, 'start') : undefined,
    call_start_time_before: end ? naiveDateToUtc(end, timeZone, 'end') : undefined,
    limit,
    sort_by_call_start_time: Sort.SORT_DESC,
  };
};

export const useAgentRecentEvaluations = (args: UseAgentRecentEvaluationsArgs) => {
  const request = buildRecentEvaluationsRequest(args);

  return useQuery({
    queryKey: [
      ...analyticsQueryKeys.all,
      'agents',
      'recent-evaluations',
      request,
      args.timeZone,
    ] as const,
    queryFn: async (): Promise<Evaluation[]> => {
      const response = await evaluationService.listEvaluations(request!);
      return response.evaluations ?? [];
    },
    enabled: !!request,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};
